import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const COULEUR = '#1C2833';
const ROUGE = '#C0392B';
const VERT = '#3B6D11';
const BLEU = '#185FA5';
const ORANGE = '#854F0B';

const STATUTS_COMMANDE = ['En attente', 'Confirmée', 'Livrée', 'Annulée'];
const PERIODES = ['Ce mois', 'Aujourd\'hui', 'Cette semaine', 'Tout'];

export default function Commandes({ onClose }) {
  const [commandes, setCommandes] = useState([]);
  const [produits, setProduits] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtres
  const [periode, setPeriode] = useState('Ce mois');
  const [filtreCommercial, setFiltreCommercial] = useState('Tous');
  const [filtreStatut, setFiltreStatut] = useState('Tous');
  const [commandeOuverte, setCommandeOuverte] = useState(null);

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    setLoading(true);
    try {
      const [cmdSnap, prodSnap, commSnap] = await Promise.all([
        getDocs(collection(db, 'commandes')),
        getDocs(collection(db, 'produits')),
        getDocs(query(collection(db, 'users'), where('role', '==', 'commercial'))),
      ]);
      const cmds = cmdSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      cmds.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      setCommandes(cmds);
      setProduits(prodSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCommerciaux(commSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.log('Commandes:', e); }
    setLoading(false);
  };

  const prixProduit = (produitId) => {
    const p = produits.find(pr => pr.id === produitId);
    return p && p.prix != null ? Number(p.prix) : null;
  };

  const montantCommande = (cmd) => {
    if (!Array.isArray(cmd.lignes)) return { montant: 0, complet: false };
    let montant = 0;
    let complet = cmd.lignes.length > 0;
    cmd.lignes.forEach(l => {
      const prix = prixProduit(l.produitId);
      const qte = parseFloat(l.quantite) || 0;
      if (prix != null) montant += prix * qte;
      else complet = false;
    });
    if (cmd.commandeAutre) complet = false;
    return { montant, complet };
  };

  const dansPeriode = (cmd) => {
    if (periode === 'Tout') return true;
    const d = cmd.date ? new Date(cmd.date + 'T00:00:00') : (cmd.createdAt?.toDate ? cmd.createdAt.toDate() : null);
    if (!d) return false;
    const now = new Date();
    if (periode === 'Aujourd\'hui') return d.toDateString() === now.toDateString();
    if (periode === 'Cette semaine') {
      const debut = new Date(now); debut.setDate(now.getDate() - 7);
      return d >= debut;
    }
    // Ce mois
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  };

  const commandesFiltrees = commandes.filter(c =>
    dansPeriode(c)
    && (filtreCommercial === 'Tous' || c.commercialId === filtreCommercial)
    && (filtreStatut === 'Tous' || (c.statut || 'En attente') === filtreStatut)
  );

  // KPIs de la sélection
  const nbArticles = commandesFiltrees.reduce((s, c) =>
    s + (Array.isArray(c.lignes) ? c.lignes.reduce((s2, l) => s2 + (parseFloat(l.quantite) || 0), 0) : 0), 0);
  const caEstime = commandesFiltrees
    .filter(c => (c.statut || 'En attente') !== 'Annulée')
    .reduce((s, c) => s + montantCommande(c).montant, 0);
  const enAttente = commandesFiltrees.filter(c => (c.statut || 'En attente') === 'En attente').length;

  // Top produits sur la sélection
  const topProduits = {};
  commandesFiltrees.filter(c => (c.statut || 'En attente') !== 'Annulée').forEach(c => {
    (c.lignes || []).forEach(l => {
      topProduits[l.nom] = (topProduits[l.nom] || 0) + (parseFloat(l.quantite) || 0);
    });
  });
  const topProduitsListe = Object.entries(topProduits).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const changerStatut = async (cmd, statut) => {
    try {
      await updateDoc(doc(db, 'commandes', cmd.id), { statut });
      setCommandes(cs => cs.map(c => c.id === cmd.id ? { ...c, statut } : c));
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  const statutStyle = (statut) => ({
    'En attente': { bg: '#FAEEDA', fg: ORANGE },
    'Confirmée': { bg: '#E6F1FB', fg: BLEU },
    'Livrée': { bg: '#EAF3DE', fg: VERT },
    'Annulée': { bg: '#FCEBEB', fg: ROUGE },
  }[statut || 'En attente'] || { bg: '#f0f0f0', fg: '#666' });

  const getNomComm = (id) => {
    const c = commerciaux.find(x => x.id === id);
    return c ? `${c.prenom || ''} ${c.nom || ''}`.trim() : '';
  };

  return (
    <div style={styles.app}>
      <div style={styles.topbar}>
        <button onClick={onClose} style={styles.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <div style={styles.topbarTitle}>🛒 Commandes</div>
          <div style={styles.topbarSub}>{commandesFiltrees.length} commande(s) · {periode}</div>
        </div>
        <button style={styles.refreshBtn} onClick={charger}>↻</button>
      </div>

      {/* KPIs */}
      <div style={styles.kpiGrid}>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiVal, color: COULEUR }}>{commandesFiltrees.length}</div>
          <div style={styles.kpiLabel}>Commandes</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiVal, color: BLEU }}>{Math.round(nbArticles).toLocaleString('fr-FR')}</div>
          <div style={styles.kpiLabel}>Articles</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiVal, color: VERT, fontSize: caEstime >= 1000000 ? 16 : 20 }}>{Math.round(caEstime).toLocaleString('fr-FR')}</div>
          <div style={styles.kpiLabel}>CA estimé (DA)</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiVal, color: ORANGE }}>{enAttente}</div>
          <div style={styles.kpiLabel}>En attente</div>
        </div>
      </div>

      {/* FILTRES */}
      <div style={styles.filtresWrap}>
        <div style={styles.chipsRow}>
          {PERIODES.map(p => (
            <button key={p} style={{ ...styles.chip, ...(periode === p ? styles.chipActive : {}) }}
              onClick={() => setPeriode(p)}>{p}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <select style={styles.select} value={filtreCommercial} onChange={e => setFiltreCommercial(e.target.value)}>
            <option value="Tous">Tous les commerciaux</option>
            {commerciaux.map(c => <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>)}
          </select>
          <select style={styles.select} value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)}>
            <option value="Tous">Tous statuts</option>
            {STATUTS_COMMANDE.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div style={styles.listWrap}>
        {loading && <div style={styles.empty}>Chargement...</div>}
        {!loading && commandesFiltrees.length === 0 && (
          <div style={styles.empty}>
            <div style={{ fontSize: 32 }}>🛒</div>
            <div style={{ marginTop: 8 }}>Aucune commande sur cette période</div>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Les commandes saisies lors des visites apparaîtront ici</div>
          </div>
        )}

        {/* TOP PRODUITS */}
        {!loading && topProduitsListe.length > 0 && (
          <div style={styles.topCard}>
            <div style={styles.topTitle}>🏆 Top produits sur la période</div>
            {topProduitsListe.map(([nom, qte], i) => (
              <div key={i} style={styles.topRow}>
                <span style={{ fontSize: 12, color: '#444' }}>{i + 1}. {nom}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: COULEUR }}>{Math.round(qte).toLocaleString('fr-FR')}</span>
              </div>
            ))}
          </div>
        )}

        {commandesFiltrees.map(cmd => {
          const { montant, complet } = montantCommande(cmd);
          const st = statutStyle(cmd.statut);
          const ouverte = commandeOuverte === cmd.id;
          return (
            <div key={cmd.id} style={styles.cmdCard} onClick={() => setCommandeOuverte(ouverte ? null : cmd.id)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={styles.cmdClient}>{cmd.clientNom}</div>
                  <div style={styles.cmdSub}>
                    {cmd.commercialNom || getNomComm(cmd.commercialId)} · {cmd.date ? new Date(cmd.date + 'T00:00:00').toLocaleDateString('fr-FR') : ''}
                    {cmd.dateLivraison ? ` · 🚚 ${new Date(cmd.dateLivraison + 'T00:00:00').toLocaleDateString('fr-FR')}` : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {montant > 0 && (
                    <div style={{ fontSize: 13, fontWeight: 700, color: VERT }}>
                      {Math.round(montant).toLocaleString('fr-FR')} DA{!complet ? ' +' : ''}
                    </div>
                  )}
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: st.bg, color: st.fg }}>
                    {cmd.statut || 'En attente'}
                  </span>
                </div>
              </div>

              {ouverte && (
                <div style={styles.cmdDetail} onClick={e => e.stopPropagation()}>
                  {(cmd.lignes || []).map((l, i) => {
                    const prix = prixProduit(l.produitId);
                    return (
                      <div key={i} style={styles.ligneRow}>
                        <span style={{ fontSize: 12, color: '#444', flex: 1 }}>{l.nom}</span>
                        <span style={{ fontSize: 12, color: '#888' }}>{l.quantite} {l.unite || ''}</span>
                        {prix != null && (
                          <span style={{ fontSize: 12, fontWeight: 600, color: COULEUR, minWidth: 80, textAlign: 'right' }}>
                            {Math.round(prix * (parseFloat(l.quantite) || 0)).toLocaleString('fr-FR')} DA
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {cmd.commandeAutre && (
                    <div style={{ fontSize: 12, color: '#666', background: '#f9f9f9', borderRadius: 6, padding: '6px 8px', marginTop: 4 }}>
                      ✏️ Autre : {cmd.commandeAutre}
                    </div>
                  )}
                  {!complet && montant > 0 && (
                    <div style={{ fontSize: 10, color: '#aaa', marginTop: 4 }}>
                      + : certains articles sans prix défini — montant partiel
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                    {STATUTS_COMMANDE.map(s => {
                      const sst = statutStyle(s);
                      const actif = (cmd.statut || 'En attente') === s;
                      return (
                        <button key={s}
                          style={{ ...styles.statutBtn, background: actif ? sst.fg : sst.bg, color: actif ? '#fff' : sst.fg }}
                          onClick={() => changerStatut(cmd, s)}>
                          {actif ? '✓ ' : ''}{s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', height: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif', overflow: 'hidden' },
  topbar: { background: COULEUR, padding: '14px 16px', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 },
  backBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  topbarTitle: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 11, opacity: 0.7, marginTop: 2 },
  refreshBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, width: 32, height: 32, color: '#fff', fontSize: 15, cursor: 'pointer', flexShrink: 0 },
  kpiGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6, padding: '10px 12px 0' },
  kpiCard: { background: '#fff', borderRadius: 10, padding: '10px 6px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  kpiVal: { fontSize: 20, fontWeight: 700 },
  kpiLabel: { fontSize: 9, color: '#888', marginTop: 2 },
  filtresWrap: { padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  chipsRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  chip: { padding: '6px 12px', border: '1px solid #ddd', borderRadius: 99, background: '#fff', color: '#666', fontSize: 12, cursor: 'pointer' },
  chipActive: { border: `1px solid ${COULEUR}`, background: COULEUR, color: '#fff', fontWeight: 600 },
  select: { flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 12, background: '#fff', fontFamily: 'system-ui' },
  listWrap: { flex: 1, overflowY: 'auto', padding: '0 12px 20px', display: 'flex', flexDirection: 'column', gap: 8 },
  empty: { textAlign: 'center', color: '#888', padding: '2.5rem 1rem', background: '#fff', borderRadius: 10 },
  topCard: { background: '#fff', borderRadius: 10, padding: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  topTitle: { fontSize: 12, fontWeight: 600, color: COULEUR, marginBottom: 8 },
  topRow: { display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f5f5f5' },
  cmdCard: { background: '#fff', borderRadius: 10, padding: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer' },
  cmdClient: { fontSize: 13, fontWeight: 600, color: '#1C2833' },
  cmdSub: { fontSize: 11, color: '#888', marginTop: 2 },
  cmdDetail: { marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0', cursor: 'default' },
  ligneRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #fafafa' },
  statutBtn: { padding: '6px 10px', border: 'none', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer' },
};
